import {Component, computed, effect, input, output, signal, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatInputModule} from "@angular/material/input"
import {ToggleComponent} from "@app/common/components/buttons/toggle/toggle.component"
import {InputContainerComponent} from "@app/common/components/inputs/input-container/input-container.component"
import {NumericInputComponent} from "@app/common/components/inputs/numeric-input/numeric-input.component"
import {StringInputComponent} from "@app/common/components/inputs/string-input/string-input.component"

interface ObjectEntry {
    key: string
    value: boolean | number | string | object | null | undefined
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-json-input",
    templateUrl: "./json-input.component.html",
    styleUrls: ["./json-input.component.scss"],
    imports: [MatFormFieldModule, MatInputModule, FormsModule, ToggleComponent, NumericInputComponent, StringInputComponent, InputContainerComponent],
})
export class JSONInputComponent {
    readonly $validate = input<(x: unknown) => boolean>(undefined, {alias: "validate"})
    readonly valueChange = output<boolean | number | string | object | null | undefined>()
    readonly $decimalPlaces = input<number>(undefined, {alias: "decimalPlaces"})
    readonly $value = input<unknown>(undefined, {alias: "value"})
    readonly $editSingleParamsEnabled = input(true, {alias: "editSingleParams"})

    readonly $objectEntries = signal<ObjectEntry[]>([])
    readonly $editSingleParams = signal(false)
    private readonly $editing = signal(false)
    private readonly $valueString = signal<string | null | undefined>(undefined)
    readonly $editValid = signal(true)
    private readonly $_editString = signal<string | null | undefined>(null)

    constructor() {
        effect(() => {
            const value = this.$value()
            this.applyValue(value)
        })
    }

    private applyValue(value: unknown) {
        this.$editSingleParams.set(this.$editSingleParamsEnabled() && this.canEditSingleParams(value))
        this.$objectEntries.set(this.$editSingleParams() ? Object.entries(value as object).map(([key, value]) => ({key, value})) : [])
        this.$valueString.set(JSON.stringify(value))
    }

    readonly $editString = computed(() => {
        if (this.$editing()) {
            return this.$_editString()
        }
        return this.$valueString()
    })

    setEditString(str: string) {
        if (this.$editing()) {
            this.$_editString.set(str)
        }
        try {
            const value = JSON.parse(str)
            const validate = this.$validate()
            const isValid = validate ? validate(value) : true
            this.$editValid.set(isValid)
            if (isValid) {
                this.valueChange.emit(value)
            }
        } catch {
            this.$editValid.set(false)
        }
    }

    updateEntry(key: string, value: boolean | number | string | object | null | undefined) {
        if (value === null || value === undefined) return
        const newEntries = this.$objectEntries().map((entry) => (entry.key === key ? {...entry, value} : entry))
        const newValue = this.entriesToObject(newEntries)

        const validate = this.$validate()
        if (validate && !validate(newValue)) return

        this.$objectEntries.set(newEntries)
        this.applyValue(newValue)
        this.valueChange.emit(newValue)
    }

    onFocus() {
        this.$_editString.set(this.$valueString())
        this.$editing.set(true)
        this.$editValid.set(true)
    }

    onBlur() {
        this.$editing.set(false)
        this.$_editString.set(null)
        this.$editValid.set(true)
    }

    private canEditSingleParams(value: unknown): boolean {
        if (typeof value !== "object" || value === null) return false
        return Object.values(value).every((v) => ["boolean", "number", "string", "object", "undefined"].includes(typeof v))
    }

    private entriesToObject = (entries: ObjectEntry[]) => Object.fromEntries(entries.map(({key, value}) => [key, value]))
}
